import React, { FC, useEffect, useState, useRef } from 'react';
import DOMPurify from 'isomorphic-dompurify';
import type { LogoContainerProps, LogoContainerAsLinkProps } from './LogoContainer.types';
import { StyledLogoContainer } from './LogoContainer.styles';
import clsx from 'clsx';
import { getLogoName } from '../../utils/helper';

export const LogoContainer: FC<LogoContainerProps> = (props: LogoContainerProps) => {
  const {
    brand,
    aspectRatio,
    width,
    color,
    height,
    alignment = 'Centered',
    isLink,
    linkUrl,
    hoverColor,
    backgroundColor,
    ariaRole,
    logopath,
    aspectMode,
    brandFullName,
    ...rest
  } = props;
  const [svgContent, setSvgContent] = useState<string>('');
  const svgContainerRef = useRef<HTMLSpanElement>(null);

  const logoName = getLogoName(brand);
  const brandName = brandFullName?.trim() || logoName;

  const logoUrl =
    logopath || `${process.env['NEXT_PUBLIC_ASSET_DOMAIN']}/mi-assets/mi-global/brand-logos/${logoName}.svg`;

  const fetchSvg = async (url: string): Promise<string | null> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch SVG: ${response.statusText}`);
      }
      return await response.text();
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  useEffect(() => {
    const fetchSvgContent = async () => {
      const content = await fetchSvg(logoUrl);
      if (content) {
        setSvgContent(content);
        updateSvgContent(content);
      } else {
        setSvgContent('');
      }
    };

    fetchSvgContent();
  }, [brand]);

  const updateSvgContent = (content: string) => {
    if (content) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(content, 'image/svg+xml');
      const svgElement = doc.querySelector('svg');

      const [scaleX, scaleY] = aspectRatio.split(':');

      if (svgElement !== null) {
        const wsf = Number(svgElement.getAttribute(`wsf-${scaleX}x${scaleY}`)).toFixed(2);
        const hsf = Number(svgElement.getAttribute(`hsf-${scaleX}x${scaleY}`)).toFixed(2);

        const scaledWidth = width * Number(wsf);
        const scaledHeight = height * Number(hsf);

        svgElement.style.width = `${scaledWidth}px`;
        svgElement.style.height = `${scaledHeight}px`;
        // Use setAttribute instead of direct assignment as preserveAspectRatio is readonly
        svgElement.setAttribute('preserveAspectRatio', aspectMode || '');
        svgElement.setAttribute('role', 'img');
        svgElement.setAttribute('aria-label', brandName);
        const stringifiedSVG: string = new XMLSerializer().serializeToString(svgElement);
        setSvgContent(stringifiedSVG);
      }
    } else {
      console.log('No SVG elements found.');
    }
  };

  if (svgContainerRef.current && svgContent) {
    while (svgContainerRef.current.firstChild) {
      // Clear existing content safely
      svgContainerRef.current.removeChild(svgContainerRef.current.firstChild);
    }

    // Sanitize and get DOM node instead of using innerHTML
    const sanitizedNode = DOMPurify.sanitize(svgContent, {
      RETURN_DOM_FRAGMENT: true,
    }) as DocumentFragment;

    // Safely append the sanitized DOM node
    svgContainerRef.current.appendChild(sanitizedNode);
  }

  return (
    <StyledLogoContainer
      data-testid="ui-library-LogoContainer"
      className={clsx(backgroundColor)}
      data-component-name="m-ui-library-LogoContainer"
      as={isLink ? 'a' : 'span'}
      href={linkUrl || undefined}
      data-logoid={brand}
      containerWidth={width}
      containerHeight={height}
      isLink={isLink || false}
      hoverColor={hoverColor || ''}
      color={color || ''}
      {...rest}
    >
      <span
        ref={svgContainerRef}
        role={ariaRole}
        className={clsx(
          { 'bottom-left': alignment === 'Bottom Left' },
          { 'center-left': alignment === 'Center Left' },
          { 'bottom-center': alignment === 'Bottom Centered' },
          { centered: alignment === 'Centered' },
          { 'color-logo': color === 'color' },
          { 'background-aware-logo': color === 'background-aware' },
          { 'alternate-logo': color === 'alternate-logo' }
        )}
      ></span>
      {isLink && (rest as LogoContainerAsLinkProps)?.target === '_blank' && (
        <span className="sr-only">(opens in new window)</span>
      )}
    </StyledLogoContainer>
  );
};
